import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { Button } from '../components/ui/button';
import PackingVisualization3D from '../components/PackingVisualization3D';
import CarrierLogo from '../components/CarrierLogo';
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, HelpCircle, X, Search,
  Box as BoxIcon, Receipt, ExternalLink, Cuboid, Truck, MapPin,
} from 'lucide-react';

// One invoice, every shipment on it. The overview can only show the worst few;
// this is where you go through the rest.

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
// Regional-indicator pair: 'FR' -> the French flag, no asset needed.
const flag = (iso) => (/^[A-Za-z]{2}$/.test(iso || '')
  ? String.fromCodePoint(...iso.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)))
  : '');
const SEV = {
  check: { label: 'Check this', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', Icon: AlertTriangle },
  warn:  { label: 'Caveat', cls: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300', Icon: HelpCircle },
  info:  { label: 'No check', cls: 'bg-muted text-muted-foreground', Icon: HelpCircle },
  ok:    { label: 'Reconciles', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300', Icon: CheckCircle2 },
};
const REASON = {
  fuel:      ['Fuel', 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'],
  price:     ['Price', 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'],
  return:    ['Returned', 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'],
  surcharge: ['Surcharge', 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'],
  weight:    ['Weight', 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'],
  parcels:   ['Parcels', 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300'],
  carrier:   ['Carrier', 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-300'],
};

const CATEGORY = {
  FRT: ['Freight', 'text-foreground'],
  FEE: ['Fee', 'text-muted-foreground'],
  FUE: ['Fuel', 'text-muted-foreground'],
  SUR: ['Surcharge', 'text-amber-700 dark:text-amber-400'],
  GOV: ['Import VAT', 'text-muted-foreground'],
  BRK: ['Brokerage', 'text-muted-foreground'],
};

function Detail({ lineId, onClose }) {
  const [d, setD] = useState(null);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    setD(null); setSim(null); setSimLoading(true);
    axiosInstance.get(`api/carrier-invoices/shipment/${lineId}/`)
      .then(r => setD(r.data)).catch(() => setD({ error: true }));
    // The pack is the point of opening this, so it loads on its own rather
    // than behind a button. It is a second request so the charges and the
    // Odoo delivery render immediately while the engine runs.
    axiosInstance.get(`api/carrier-invoices/shipment/${lineId}/`, { params: { simulate: 1 } })
      .then(r => setSim(r.data.simulation || { error: 'nothing came back' }))
      .catch(() => setSim({ error: 'the re-pack failed' }))
      .finally(() => setSimLoading(false));
  }, [lineId]);

  // The 3D needs positioned items, which only a live pack produces - a full
  // engine pass, so it waits to be asked for.
  const repack = () => {
    setSimLoading(true);
    axiosInstance.get(`api/carrier-invoices/shipment/${lineId}/`, { params: { simulate: 1 } })
      .then(r => setSim(r.data.simulation || { error: 'nothing came back' }))
      .catch(() => setSim({ error: 'the re-pack failed' }))
      .finally(() => setSimLoading(false));
  };

  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const f = d?.finding || {};
  const od = d?.odoo;
  const ours = d?.ours;
  const charges = d?.charges || [];
  const theirTotal = charges.filter(c => !['GOV', 'BRK', 'EXM'].includes(c.category))
    .reduce((a, c) => a + c.amount_eur, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 md:p-8"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-6xl rounded-xl border border-border bg-background shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-border bg-background/95 backdrop-blur px-5 py-3.5 rounded-t-xl">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold font-mono">{f.order_number || '—'}</h2>
              {f.country && (
                <span className="rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted">
                  <span className="mr-1">{flag(f.country)}</span>{f.country}
                </span>
              )}
              {f.severity && (
                <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${SEV[f.severity]?.cls}`}>
                  {React.createElement(SEV[f.severity]?.Icon || HelpCircle, { className: 'h-3 w-3' })}
                  {SEV[f.severity]?.label}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {d?.invoice?.carrier} {d?.invoice?.number} · {d?.invoice?.date}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {f.order_number && (
              <a href={`/packing-simulator?order=${encodeURIComponent(String(f.order_number).replace('#', ''))}`}
                 target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Simulator
                </Button>
              </a>
            )}
            <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!d ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {f.verdict && (
              <p className="text-sm leading-relaxed rounded-lg border border-border bg-muted/30 p-3">{f.verdict}</p>
            )}

            {f.fuel_anomaly_eur && (
              <p className="text-sm rounded-lg border border-amber-300 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/20 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-700 dark:text-amber-400" />
                <span>
                  <span className="font-medium">The fuel on this shipment is {eur(f.fuel_anomaly_eur)} more
                  than its own stated rate produces.</span>{' '}
                  Every other fuel line on this invoice follows the formula, so this is not something
                  our rates can explain. Worth putting to the carrier.
                </span>
              </p>
            )}

            {f.zone_surcharge_note && (
              <p className="text-xs rounded-lg border border-border bg-muted/20 p-2.5 flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-px text-muted-foreground" />
                <span>
                  {f.zone_surcharge_note}{' '}
                  <span className="text-muted-foreground">
                    Our own figure prices the same postcode, so a destination-zone charge is
                    expected here rather than a finding.
                  </span>
                </span>
              </p>
            )}

            {f.destination && (
              <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 mt-px" />
                <span>
                  <span className="text-foreground">{f.destination}</span>
                  {f.country ? `, ${f.country}` : ''}
                  <span className="text-muted-foreground/70"> — where {d?.invoice?.carrier} says it went</span>
                  {f.zone_surcharge_note && (
                    <span className="text-muted-foreground/70"> · priced on that postcode on both sides</span>
                  )}
                </span>
              </p>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* THEIR SIDE */}
              <section className="rounded-lg border border-border">
                <header className="flex items-center gap-2 border-b border-border px-3.5 py-2">
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">What they charged</h3>
                  <CarrierLogo name={d?.invoice?.carrier} height={13} showName={false} />
                  <span className="ml-auto text-sm font-semibold tabular-nums">{eur(theirTotal)}</span>
                </header>
                <table className="w-full text-xs">
                  <tbody>
                    {charges.map(c => {
                      const [label, cls] = CATEGORY[c.category] || [c.category, ''];
                      return (
                        <tr key={c.id} className="border-b border-border/50 last:border-0">
                          <td className="px-3.5 py-1.5 whitespace-nowrap">
                            <span className={`text-[10px] uppercase tracking-wide ${cls}`}>{label}</span>
                            <div className="font-mono text-[11px] text-muted-foreground">{c.code}</div>
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">
                            {c.description || c.service}
                            {c.glossary && (
                              <div className="text-[10px] text-muted-foreground/80 mt-0.5"
                                   title={`${c.glossary.meaning}\n\nDo we model it? ${c.glossary.modelled}`}>
                                <span className="font-medium text-foreground/70">{c.glossary.name}</span>
                                {' · '}{c.glossary.meaning.slice(0, 90)}
                                {c.glossary.meaning.length > 90 ? '…' : ''}
                              </div>
                            )}
                            {c.weight_billed > 0 && <div className="text-[10px]">{c.weight_billed.toFixed(2)} kg</div>}
                          </td>
                          <td className="px-3.5 py-1.5 text-right tabular-nums whitespace-nowrap">{eur(c.amount_eur)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>

              {/* OUR SIDE */}
              <section className="rounded-lg border border-border">
                <header className="flex items-center gap-2 border-b border-border px-3.5 py-2">
                  <BoxIcon className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium">What we packed</h3>
                  {f.same_carrier_eur != null && (
                    <span className="ml-auto text-sm font-semibold tabular-nums">{eur(f.same_carrier_eur)}</span>
                  )}
                </header>
                {!ours ? (
                  <p className="px-3.5 py-6 text-xs text-muted-foreground text-center">No analysis record for this shipment.</p>
                ) : (
                  <div className="px-3.5 py-2.5 text-xs space-y-2">
                    <p className="text-sm">{ours.solution}</p>
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
                      <dt>Carrier we hold</dt>
                      <dd className="text-foreground text-right"><CarrierLogo name={ours.carrier} height={13} /></dd>
                      {f.same_carrier_name && f.same_carrier_name !== ours.carrier && (
                        <><dt>Priced on</dt>
                          <dd className="text-foreground text-right"><CarrierLogo name={f.same_carrier_name} height={13} /></dd></>
                      )}
                      <dt>Parcels</dt>
                      <dd className="text-foreground text-right">
                        {ours.parcel_dims?.length || ours.bins_count} ours / {f.billed_parcels} billed
                      </dd>
                      <dt>Chargeable weight</dt>
                      <dd className="text-foreground text-right">
                        {f.our_real_kg != null ? `${f.our_real_kg} kg real` : '—'}
                        {f.our_vol_kg != null && ` · ${f.our_vol_kg} kg vol`}
                      </dd>
                      <dt>Items</dt><dd className="text-foreground text-right">{ours.items_count}</dd>
                      {ours.surcharge_total > 0 && (
                        <><dt>Our surcharges</dt><dd className="text-amber-700 dark:text-amber-400 text-right">{eur(ours.surcharge_total)}</dd></>
                      )}
                    </dl>
                    {f.better_carrier && (
                      <div className="rounded-md border border-teal-300/60 dark:border-teal-900/60 bg-teal-50/60 dark:bg-teal-950/20 px-2 py-1.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <CarrierLogo name={f.better_carrier} height={13} />
                          <span className="text-teal-800 dark:text-teal-300 font-medium tabular-nums">
                            {eur(f.better_carrier_eur)}
                          </span>
                          <span className="text-muted-foreground">
                            — {eur(f.better_saving_eur)} cheaper on these parcels
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="pt-1 space-y-1">
                      {(ours.parcel_dims || []).map((b, i) => (
                        <div key={i} className="flex items-baseline gap-2 rounded bg-muted/50 px-2 py-1">
                          <span className="font-mono text-[11px]">{b.w}×{b.h}×{b.l} cm</span>
                          <span className="text-[11px] text-muted-foreground">{b.kg} kg</span>
                          <span className="ml-auto text-[10px] text-muted-foreground truncate">{b.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* WHAT ACTUALLY LEFT THE WAREHOUSE */}
            <section className="rounded-lg border border-border">
              <header className="flex items-center gap-2 border-b border-border px-3.5 py-2">
                <Truck className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">What ops shipped</h3>
                {od?.picking && (od.picking_url ? (
                  <a href={od.picking_url} target="_blank" rel="noreferrer"
                     className="ml-auto font-mono text-xs text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
                     title="Open this delivery in Odoo">
                    {od.picking}<ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{od.picking}</span>
                ))}
              </header>
              {!od ? (
                <p className="px-3.5 py-4 text-xs text-muted-foreground">Looking for the Odoo delivery…</p>
              ) : od.error ? (
                <p className="px-3.5 py-4 text-xs text-muted-foreground">{od.error}</p>
              ) : (
                <div className="px-3.5 py-3 grid gap-4 md:grid-cols-3 text-xs">
                  <dl className="space-y-1">
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Booked with</dt>
                      <dd className="text-right"><CarrierLogo name={od.carrier} height={13} /></dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">State</dt>
                      <dd className="text-right">{od.state}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Shipped</dt>
                      <dd className="text-right">{(od.date_done || '').slice(0, 10) || '—'}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Weight in Odoo</dt>
                      <dd className="text-right">{od.weight_kg ? `${od.weight_kg} kg` : '—'}</dd></div>
                  </dl>
                  {od.address?.city && (
                    <div className="md:col-span-3 border-t border-border/60 pt-2">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
                        Odoo contact
                      </p>
                      <p className="leading-snug text-muted-foreground">
                        {od.address.name && <span className="text-foreground">{od.address.name}</span>}
                        {od.address.street && ` · ${od.address.street}`}
                        {' · '}{[od.address.zip, od.address.city, od.address.region, od.address.country]
                          .filter(Boolean).join(', ')}
                      </p>
                      {f.destination && od.address.zip &&
                       !f.destination.includes(od.address.zip) && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 flex gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                          Odoo holds a different postcode from the one the carrier delivered to. The
                          carrier&rsquo;s address is the shipment; this one is the customer on file.
                        </p>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                      Parcels ops created ({od.tracking_list?.length || od.package_count || 0})
                    </p>
                    {(od.tracking_list || []).length === 0 ? (
                      <p className="text-muted-foreground">No tracking recorded on the picking.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {od.tracking_list.map((t) => {
                          const billed = (d?.charges || []).some(c => c.tracking === t);
                          return (
                            <span key={t}
                                  title={billed ? 'This parcel is on the invoice' : 'Not billed on this invoice'}
                                  className={`font-mono text-[11px] rounded px-1.5 py-0.5 ${
                                    billed ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300'
                                           : 'bg-muted text-muted-foreground'}`}>
                              {t}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {od.package_count > 0 && (
                      <p className="text-muted-foreground mt-1">{od.package_count} package(s) recorded</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">On the picking</p>
                    <ul className="space-y-0.5">
                      {(od.lines || []).slice(0, 8).map((l, i) => (
                        <li key={i} className="flex justify-between gap-2">
                          <span className="truncate">{l.product}</span>
                          <span className="tabular-nums text-muted-foreground shrink-0">×{l.qty}</span>
                        </li>
                      ))}
                    </ul>
                    {(od.other_pickings || []).length > 0 && (
                      <p className="text-muted-foreground mt-1">
                        also {od.other_pickings.map(p => `${p.name} (${p.state})`).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* THE PACK ITSELF */}
            <section className="rounded-lg border border-border">
              <header className="flex items-center gap-2 border-b border-border px-3.5 py-2">
                <Cuboid className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">What we packed, in full</h3>
                {sim?.error && (
                  <Button variant="outline" size="sm" className="ml-auto h-7 text-xs"
                          disabled={simLoading} onClick={repack}>Try again</Button>
                )}
              </header>
              {!sim ? (
                <p className="px-3.5 py-5 text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Re-packing the order, because a record stores the parcels but not what sat where inside them.
                </p>
              ) : sim.error ? (
                <p className="px-3.5 py-5 text-xs text-amber-700 dark:text-amber-400">{sim.error}</p>
              ) : (
                <div className="px-3.5 py-3 space-y-3">
                  <p className="text-sm">
                    {sim.description}
                    <span className="text-xs text-muted-foreground ml-2">
                      ({eur(sim.shipping_cost)} shipping + {eur(sim.packaging_cost)} packaging)
                    </span>
                  </p>
                  {(sim.products || []).length > 0 && (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                          <th className="text-left font-medium py-1">Product</th>
                          <th className="text-right font-medium py-1">Qty</th>
                          <th className="text-right font-medium py-1">Dimensions</th>
                          <th className="text-right font-medium py-1">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sim.products.map((p, i) => (
                          <tr key={i} className="border-b border-border/40 last:border-0">
                            <td className="py-1 pr-2">{p.title}</td>
                            <td className="py-1 text-right tabular-nums">{p.quantity}</td>
                            <td className="py-1 text-right tabular-nums font-mono text-[11px]">
                              {p.dimensions ? `${p.dimensions.width}×${p.dimensions.height}×${p.dimensions.length}` : '—'}
                            </td>
                            <td className="py-1 text-right tabular-nums">{p.weight ? `${p.weight} kg` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {(sim.bins || []).map((b, i) => (
                    <div key={i}>
                      <p className="text-xs font-medium mb-1">
                        {b.bin_name}
                        <span className="text-muted-foreground font-normal ml-2">
                          {b.items?.length || 0} items · {eur(b.cost)}
                          {b.rate_method ? ` · ${b.rate_method}` : ''}
                        </span>
                      </p>
                      <PackingVisualization3D bin={b} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// The invoice in one look, before anyone starts scrolling. Every figure here
// describes the WHOLE file, never the filter below it.
function GeneralCheck({ s, fuel, carrier, onFilter, onReason }) {
  const w = s.weight_bands || {};
  const pct = (n) => (s.weighed ? Math.round((n / s.weighed) * 100) : 0);
  const bar = [
    ['match', w.match, 'bg-teal-500', 'within 15% of ours'],
    ['dim', w.dim, 'bg-blue-400', '1.15-2x ours, a dimensional weight'],
    ['over', w.over, 'bg-amber-500', 'more than twice our weight'],
    ['under', w.under, 'bg-muted-foreground/40', 'below ours, in our favour'],
  ].filter(([, n]) => n > 0);

  return (
    <section className="mt-5 rounded-lg border border-border bg-card p-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Their charge against our rates</p>
          {s.stale_card ? (
            <p className="text-sm text-muted-foreground">
              This invoice was billed before our current rate card came into force, so there is no
              card to price it against. The weight and parcel-count checks below still stand.
            </p>
          ) : s.delta_pct == null ? (
            <p className="text-sm text-muted-foreground">Nothing on this invoice could be priced.</p>
          ) : (
            <>
              <p className="text-2xl font-semibold tabular-nums">
                <span className={s.delta_eur > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-teal-700 dark:text-teal-400'}>
                  {s.delta_pct > 0 ? '+' : ''}{s.delta_pct}%
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                our card {eur(s.our_card_eur)} against {eur(s.charged_eur)} charged, over {s.priced} shipment{s.priced === 1 ? '' : 's'}
                {s.delta_eur > 0 ? ' — we over-estimate this carrier' : ' — we under-estimate this carrier'}
              </p>
              {s.charged_base_eur != null && s.our_base_eur != null && (
                <table className="mt-2 text-[11px] tabular-nums" title="The same money as three blocks on both sides">
                  <thead><tr className="text-muted-foreground"><th className="text-left font-normal pr-3"></th><th className="text-right font-normal px-2">price</th><th className="text-right font-normal px-2">fuel</th><th className="text-right font-normal px-2">extras</th></tr></thead>
                  <tbody>
                    <tr><td className="pr-3 text-muted-foreground">{carrier || 'carrier'} billed</td><td className="text-right px-2">{eur(s.charged_base_eur)}</td><td className="text-right px-2">{eur(s.charged_fuel_eur)}</td><td className="text-right px-2">{eur(s.charged_fees_eur)}</td></tr>
                    <tr><td className="pr-3 text-muted-foreground">our card</td><td className="text-right px-2">{eur(s.our_base_eur)}</td><td className="text-right px-2">{eur(s.our_fuel_eur)}</td><td className="text-right px-2">{eur(s.our_fees_eur)}</td></tr>
                    <tr className="font-medium"><td className="pr-3 text-muted-foreground">gap</td>
                      {[['base', s.our_base_eur - s.charged_base_eur], ['fuel', s.our_fuel_eur - s.charged_fuel_eur], ['fees', s.our_fees_eur - s.charged_fees_eur]].map(([k, v]) => (
                        <td key={k} className={`text-right px-2 ${Math.abs(v) > Math.max(5, 0.05 * (s.charged_eur || 1)) ? 'text-amber-700 dark:text-amber-400' : ''}`}>{v > 0 ? '+' : ''}{eur(v)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              )}
              {s.measured_boxes > 0 && (
                <p className="text-[11px] text-muted-foreground/80 mt-1">{s.measured_boxes} box{s.measured_boxes === 1 ? '' : 'es'} taken at the volume the carrier measured (no cut stored on the record).</p>
              )}
              {s.unpriced > 0 && (
                <p className="text-[11px] text-muted-foreground/80 mt-1">
                  {s.unpriced} more still to price. Each one is a full run of the packing engine, so a
                  visit does what it can and the rest fills in as you come back.
                </p>
              )}
            </>
          )}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Fuel</p>
          {!fuel || fuel.status !== 'separable' ? (
            <p className="text-xs text-muted-foreground">
              {fuel?.detail || 'This carrier does not bill fuel as its own line.'}
            </p>
          ) : (
            <>
              <p className="text-2xl font-semibold tabular-nums">
                {fuel.stated_pct ?? fuel.implied_pct}%
                {fuel.our_pct != null && (
                  <span className="text-base font-normal text-muted-foreground">
                    {' '}vs {fuel.our_pct}% modelled
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {eur(fuel.invoiced_fuel_eur)} on {eur(fuel.fuelable_eur)} of charges
                {fuel.implied_pct != null && ` — effective ${fuel.implied_pct}%`}
              </p>
              {fuel.gap_points ? (
                <p className={`text-xs mt-0.5 ${fuel.gap_points > 0
                  ? 'text-amber-700 dark:text-amber-400' : 'text-teal-700 dark:text-teal-400'}`}>
                  We are {Math.abs(fuel.gap_points)} points {fuel.gap_points > 0 ? 'low' : 'high'}
                  {fuel.our_spread ? ` (our lanes hold ${fuel.our_spread})` : ''}.
                </p>
              ) : null}
            </>
          )}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Billed weight against ours</p>
          {s.weighed === 0 ? (
            <p className="text-sm text-muted-foreground">This file states no billed weight.</p>
          ) : (
            <>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
                {bar.map(([k, n, cls]) => <div key={k} className={cls} style={{ width: `${pct(n)}%` }} />)}
              </div>
              <ul className="mt-1.5 space-y-0.5">
                {bar.map(([k, n, cls, label]) => (
                  <li key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={`h-2 w-2 rounded-full ${cls}`} />
                    <span className="tabular-nums text-foreground">{n}</span> {label}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">What needs a person</p>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => onFilter('check')}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 hover:opacity-80">
              <AlertTriangle className="h-3 w-3" />{s.to_check} to check
            </button>
            {s.caveats > 0 && (
              <button onClick={() => onFilter('warn')}
                      className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300 hover:opacity-80">
                {s.caveats} caveat{s.caveats === 1 ? '' : 's'}
              </button>
            )}
            <button onClick={() => onFilter('ok')}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300 hover:opacity-80">
              <CheckCircle2 className="h-3 w-3" />{s.reconciled} reconcile
            </button>
            {s.not_comparable > 0 && (
              <button onClick={() => onFilter('info')}
                      className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-muted text-muted-foreground hover:opacity-80">
                {s.not_comparable} not comparable
              </button>
            )}
          </div>
          {(s.by_reason || []).length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {s.by_reason.map(b => (
                <li key={b.code} className="flex items-baseline gap-1.5 text-xs">
                  <button onClick={() => onReason(b.code)}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold hover:opacity-80 ${(REASON[b.code] || [])[1]}`}>
                    {(REASON[b.code] || [b.code])[0]}
                  </button>
                  <span className="tabular-nums text-foreground">{b.n}</span>
                  <span className="text-muted-foreground">{b.label.toLowerCase()}</span>
                  {b.eur > 0 && <span className="ml-auto tabular-nums text-muted-foreground">{eur(b.eur)}</span>}
                </li>
              ))}
            </ul>
          )}
          {s.fuel_anomalies > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
              {s.fuel_anomalies} shipment{s.fuel_anomalies === 1 ? '' : 's'} billed {eur(s.fuel_anomaly_eur)} of
              fuel beyond the rate stated on the line itself.
            </p>
          )}
          {s.parcel_mismatches > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              {s.parcel_mismatches} shipment{s.parcel_mismatches === 1 ? '' : 's'} where {carrier} billed more
              parcels than our solution has.
            </p>
          )}
        </div>
      </div>

      {(s.biggest || []).length > 0 && (
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Biggest gaps against our card</p>
          <div className="flex flex-wrap gap-1.5">
            {s.biggest.map((b, i) => (
              <span key={i} className="inline-flex items-baseline gap-1.5 rounded border border-border bg-muted/40 px-2 py-1 text-xs"
                    title={b.verdict}>
                <span className="font-mono">{b.order_number}</span>
                <span className="text-[10px] text-muted-foreground">{b.country}</span>
                <span className="text-amber-700 dark:text-amber-400 tabular-nums">+{eur(b.delta)}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// A sortable column head. Clicking cycles biggest-first, smallest-first, off.
function SortTh({ k, sort, onSort, children, title }) {
  const active = (sort || '').replace(/^[+-]/, '') === k;
  const asc = active && sort.startsWith('+');
  return (
    <th className="text-right font-medium px-3 py-2 cursor-pointer select-none hover:text-foreground whitespace-nowrap"
        title={title} onClick={() => onSort(active ? (asc ? '' : `+${k}`) : k)}>
      {children}
      <span className={`ml-1 ${active ? 'text-foreground' : 'opacity-25'}`}>{asc ? '▲' : '▼'}</span>
    </th>
  );
}

function renderInline(text) {
  // **bold** and `code` only; the analysis is written by hand, not by users.
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={i} className="text-[11px] bg-muted px-1 py-0.5 rounded">{p.slice(1, -1)}</code>;
    return p;
  });
}

export default function InvoiceShipments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('');
  const [country, setCountry] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [sort, setSort] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance.get(`api/carrier-invoices/${id}/shipments/`,
                      { params: { page, severity, country, q: q || undefined, sort: sort || undefined, reason: reason || undefined } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [id, page, severity, country, q, sort, reason]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [severity, country, q, sort, reason]);

  const inv = data?.invoice;
  const rows = data?.shipments || [];

  return (
    <div className="p-4 md:p-6 2xl:px-10">
      <button onClick={() => navigate('/carrier-invoices/files')}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-3.5 w-3.5" /> All invoices
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70 mb-0.5">Invoice Analysis</div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {inv ? `${inv.carrier} ${inv.number}` : 'Loading…'}
          </h1>
          {inv && (
            <p className="text-sm text-muted-foreground mt-1">
              {inv.date} · {eur(inv.total_eur)} · <span className="font-mono text-xs">{inv.format}</span>
              {' · '}{data.total} shipment{data.total === 1 ? '' : 's'}
            </p>
          )}
        </div>
        {inv?.has_file && (
          <a href={`${axiosInstance.defaults.baseURL}api/carrier-invoices/${id}/file/`} download>
            <Button variant="outline" size="sm">Download the file</Button>
          </a>
        )}
      </div>

      {(inv?.warnings || []).map((w, i) => (
        <p key={i} className="mt-2 text-xs text-amber-700 dark:text-amber-400 flex gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}
        </p>
      ))}

      {inv?.analysis && (
        <details open className="mt-3 rounded-lg border border-border bg-card">
          <summary className="cursor-pointer px-4 py-2 text-sm font-semibold">
            Analysis{inv.analysis_at ? <span className="ml-2 text-xs font-normal text-muted-foreground">written {new Date(inv.analysis_at).toLocaleDateString()}</span> : null}
          </summary>
          <div className="px-4 pb-4 text-sm leading-relaxed">
            {inv.analysis.split(/\n{2,}/).map((para, i) => {
              const lines = para.split('\n');
              if (lines.every((l) => /^\s*[-*] /.test(l))) {
                return <ul key={i} className="list-disc pl-5 my-2 space-y-1">{lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^\s*[-*] /, ''))}</li>)}</ul>;
              }
              if (/^#{1,3} /.test(para)) return <h3 key={i} className="font-semibold mt-3 mb-1">{para.replace(/^#{1,3} /, '')}</h3>;
              if (lines.length > 1 && lines.every((l) => l.includes('|'))) {
                const rows = lines.filter((l) => !/^\s*\|?\s*-{2,}/.test(l)).map((l) => l.split('|').map((c) => c.trim()).filter((c, k, arr) => !(k === 0 && c === '') && !(k === arr.length - 1 && c === '')));
                return (
                  <div key={i} className="overflow-x-auto my-2"><table className="text-xs w-full">
                    <thead><tr>{rows[0].map((c, k) => <th key={k} className="text-left font-semibold pr-3 pb-1 border-b border-border whitespace-nowrap">{c}</th>)}</tr></thead>
                    <tbody>{rows.slice(1).map((r, k) => <tr key={k} className="border-b border-border/40">{r.map((c, m) => <td key={m} className="pr-3 py-1 align-top">{renderInline(c)}</td>)}</tr>)}</tbody>
                  </table></div>
                );
              }
              return <p key={i} className="my-2">{renderInline(para)}</p>;
            })}
          </div>
        </details>
      )}

      {data?.summary && <GeneralCheck s={data.summary} fuel={data.fuel} carrier={inv?.carrier}
                                      onFilter={setSeverity} onReason={setReason} />}

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input className="h-8 w-56 rounded-md border border-input bg-background pl-7 pr-2 text-xs"
                 placeholder="Order, tracking or carrier…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={severity} onChange={e => setSeverity(e.target.value)}>
          <option value="">Everything</option>
          <option value="check">To check</option>
          <option value="warn">Caveats</option>
          <option value="ok">Reconciles</option>
          <option value="info">Nothing to compare</option>
        </select>
        <select className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={country} onChange={e => setCountry(e.target.value)}>
          <option value="">All destinations</option>
          {(data?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {reason && (
          <button onClick={() => setReason('')}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${(REASON[reason] || [])[1]}`}>
            {(REASON[reason] || [reason])[0]} only <X className="h-3 w-3" />
          </button>
        )}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <div className="mt-3 rounded-lg border border-border bg-card overflow-x-auto">
        {/* Auto layout, with the verdict set to 100%: the numeric columns take
            exactly what they need and the prose absorbs everything left over.
            A fixed layout with a colgroup collapsed the verdict to nothing. */}
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="text-left font-medium px-4 py-2">Order</th>
              <th className="text-left font-medium px-3 py-2">To</th>
              <SortTh k="charged" sort={sort} onSort={setSort} title="All in, then price + fuel + extras">Charged</SortTh>
              <SortTh k="ours" sort={sort} onSort={setSort} title="All in, then price + fuel + extras on our card">Our card</SortTh>
              <SortTh k="parcels" sort={sort} onSort={setSort}>Parcels</SortTh>
              <SortTh k="weight" sort={sort} onSort={setSort} title="Billed minus ours, on the basis this carrier bills">kg gap</SortTh>
              <SortTh k="delta" sort={sort} onSort={setSort}>Δ price</SortTh>
              <th className="text-left font-medium px-3 py-2 w-full">What we make of it</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(s => {
              const sv = SEV[s.severity] || SEV.info;
              return (
                <tr key={s.line_id} onClick={() => setOpen(s.line_id)}
                    className={`border-b border-border/60 last:border-0 align-top cursor-pointer hover:bg-muted/40 ${
                      s.severity === 'check' ? 'bg-amber-50/60 dark:bg-amber-950/20 border-l-[3px] border-l-amber-500' : ''}`}>
                  <td className="px-4 py-2 font-mono whitespace-nowrap">{s.order_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="rounded px-1.5 py-0.5 text-[11px] bg-muted">
                      <span className="mr-1">{flag(s.country)}</span>{s.country || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {eur(s.all_in_eur ?? s.amount_eur)}
                    {s.all_in_eur != null && (
                      <div className="text-[10px] text-muted-foreground" title="price + fuel + extras, as billed">
                        {eur(s.amount_eur)} <span className="opacity-60">+</span> {eur(s.fuel_eur || 0)} <span className="opacity-60">+</span> {eur(s.fees_eur || 0)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {s.same_carrier_eur != null ? eur(s.same_carrier_eur) : <span className="text-muted-foreground">—</span>}
                    {s.our_base_eur != null && (
                      <div className="text-[10px] text-muted-foreground" title="price + fuel + extras, our card, same blocks">
                        {eur(s.our_base_eur)} <span className="opacity-60">+</span> {eur(s.our_fuel_eur || 0)} <span className="opacity-60">+</span> {eur(s.our_fees_eur || 0)}
                        {s.box_from_carrier_measurement && <span className="ml-1" title="No cut stored on this record: the box was taken at the volume the carrier measured">(box as measured)</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-xs">
                    {s.billed_parcels}<span className="text-muted-foreground"> / {s.our_parcels ?? '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-xs">
                    {s.weight_delta_kg == null ? <span className="text-muted-foreground">—</span> : (
                      <span className={s.weight_delta_kg > 0 ? 'text-amber-700 dark:text-amber-400' : ''}>
                        {s.weight_delta_kg > 0 ? '+' : ''}{s.weight_delta_kg}
                      </span>
                    )}
                    <div className="text-[10px] text-muted-foreground">
                      {s.weight_billed ?? '—'} / {s.our_real_kg ?? s.our_kg ?? '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {s.price_delta_eur == null ? <span className="text-muted-foreground">—</span> : (
                      <span className={`font-medium ${s.price_delta_eur > 0 ? 'text-amber-700 dark:text-amber-400'
                                                                            : 'text-teal-700 dark:text-teal-400'}`}>
                        {s.price_delta_eur > 0 ? '+' : '−'}{eur(Math.abs(s.price_delta_eur))}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold mr-1.5 whitespace-nowrap ${sv.cls}`}>
                      <sv.Icon className="h-3 w-3" />{sv.label}
                    </span>
                    {(s.reasons || []).map(r => (
                      <span key={r.code}
                            className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold mr-1.5 whitespace-nowrap ${(REASON[r.code] || [])[1]}`}
                            title={r.detail || r.label}>
                        {(REASON[r.code] || [r.code])[0]}{r.eur ? ` ${eur(r.eur)}` : ''}
                      </span>
                    ))}
                    <span className="text-muted-foreground text-xs">{s.verdict}</span>
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground text-sm">
                Nothing matches those filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>
            {(data.page - 1) * data.size + 1}&ndash;{Math.min(data.page * data.size, data.total)} of {data.total}
          </span>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="h-7 text-xs"
                    disabled={data.page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="px-1">page {data.page} of {data.pages}</span>
            <Button variant="outline" size="sm" className="h-7 text-xs"
                    disabled={data.page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      {open && <Detail lineId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
